import {RequireSome} from '../types/base';
import {
  MaskControl,
  MediaPanelCardControl,
  BoundingBoxClassControl,
  AllMaskControls,
  AllBoundingBoxControls,
  BoundingBoxSliderControl,
  Style,
  LineStyle,
} from './MediaCard';

import {isGrouped} from '../util/runhelpers';

import {PartRefFromObjSchema} from '../state/views/types';
import * as PanelTypes from '../state/views/panel/types';

import classNames from 'classnames';

import * as _ from 'lodash';
import {useSelector} from '../state/hooks';
import * as React from 'react';
import {useState, useRef, useCallback, useEffect, useMemo} from 'react';
import {
  Card,
  Checkbox,
  Popup,
  List,
  Form,
  Loader,
  Tab,
  Message,
  Dropdown,
  Button,
  ButtonGroup,
} from 'semantic-ui-react';
import {RunsDataQuery, toRunsDataQuery} from '../containers/RunsDataLoader';
import * as RunHelpers from '../util/runhelpers';
import {CompareOp} from '../util/ops';
import {makeOptions} from '../util/uihelpers';
import {
  expandRangeToNElements,
  sampleSorted,
  GroupedList,
  mapGroupedLeafs,
  firstGrouped,
} from '../util/data';
import {
  MediaCardMetadata,
  keyToMediaCardType,
  MediaCardType,
  MediaString,
  MaskOptions,
} from '../types/media';
import {RunSignature, RunHistoryRow} from '../types/run';
import {CHART_SAMPLES} from '../util/constants';
import {
  classLabelMap,
  ClassLabelNode,
  getMediaKeysFromKeyInfo,
  stepsWithOneOfKeys,
  mediaTypeToComponent,
  getFirstMediaMetadata,
  WANDB_MASK_CLASS_LABEL_KEY,
  segmentationMaskColor,
  DEFAULT_CLASS_MASK_CONTROL,
  DEFAULT_ALL_MASK_CONTROL,
  WANDB_BBOX_CLASS_LABEL_KEY,
} from '../util/media';
import {SelectableAxis, StaticAxis} from '../components/Axis';

import * as Panels from '../util/panels';
import * as Query from '../util/queryts';
import Input from './Input';
import PanelError from './elements/PanelError';
import SliderInput from './elements/SliderInput';
import {INPUT_SLIDER_CLASS} from './elements/SliderInput';

import './PanelMediaBrowser.less';
import * as urls from '../util/urls';
import ModifiedDropdown from './elements/ModifiedDropdown';
import {Data} from '../state/runs/types';

import memoize from 'fast-memoize';
import HelpPopup from './elements/HelpPopup';
import {
  RepresentationTypeValues,
  RepresentationType,
} from '../types/libs/nglviewer';

import {MoleculeConfig} from './MoleculeCard';

import {RunWithHistoryAndMediaWandb} from './MediaCard';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {fuzzyMatchRegex} from '../util/fuzzyMatch';
import produce from 'immer';
import {IconSizeProp} from 'semantic-ui-react/dist/commonjs/elements/Icon/Icon';
import makeComp from '../util/profiler';

export const memoizedStepsWithKeys = memoize(stepsWithOneOfKeys);

const PANEL_TYPE = 'Media Browser';

// Global configurable
const PADDING = 6;

const DEFAULT_LAYOUT_TYPE: LayoutType = 'ALL_STACKED';

type MediaBrowserPanelProps = Panels.PanelProps<MediaBrowserPanelConfig>;

interface MediaBrowserPanelState {
  mediaBrowserWidth: number;
}

interface RangeSettings {
  min: number;
  max: number;
  current: number;
  active?: boolean;
}
export type Axis = 'step' | 'index' | 'run' | 'camera';
const AxisStrings: Axis[] = ['step', 'index', 'run', 'camera'];
type PanelModes = 'gallery' | 'grid';

export interface MediaBrowserPanelConfig {
  chartTitle?: string; //
  stepIndex?: number; // index of the step shown from each run, default: 0
  mediaIndex?: number; // index of the image shown from each run, default: 0
  mediaKeys?: string[]; // key for media, like 'examples'

  // audio-specific config
  scaleWaveformWidth?: boolean; // whether or not we scale the widths of waveforms relative to each other (if false, all will have width:100%)

  actualSize?: boolean; //
  fitToDimension?: boolean; //
  pixelated?: boolean; //

  // Settings for tiling
  columnCount?: number; //
  mode?: PanelModes; //
  gallerySettings?: {
    axis: Axis;
  };
  gridSettings?: {
    xAxis: Axis;
    yAxis: Axis;
  };
  selection?: {
    xAxis?: [number, number];
    yAxis?: [number, number];
  };

  page?: {
    start?: number;
  };

  // How to stack masks
  tileLayout?: {
    [mediaKey: string]: {
      type: LayoutType;
      maskOptions: MaskOptions[];
    };
  };

  // The number of steps to increase on the up and down buttons of the step slider
  stepStrideLength?: number;
  maxGalleryItems?: number;
  maxYAxisCount?: number;
  moleculeConfig?: MoleculeConfig;
  segmentationMaskConfig?: AllMaskControls;
  boundingBoxConfig?: AllBoundingBoxControls;
}

type LayoutType = 'ALL_STACKED' | 'MASKS_NEXT_TO_IMAGE' | 'ALL_SPLIT';

interface SizingSettings {
  columnCount: {
    min: number;
    max: number;
    current: number;
  };
}

const MAX_COL_COUNT = 20;
const DEFAULT_MAX_Y_AXIS_COUNT = 30;

function defaultColumnCount(
  mediaType: MediaCardType | null,
  currentColumnCount?: number,
  mediaCount?: number
): number {
  let defaultColCount: number;

  if (mediaCount != null && mediaCount < 4) {
    defaultColCount = mediaCount;
  } else if (mediaType === 'object3D' || mediaType === 'video') {
    defaultColCount = 4;
  } else if (mediaType === 'image') {
    defaultColCount = Math.min(8, mediaCount ?? Infinity);
  } else {
    defaultColCount = 1;
  }

  const current = currentColumnCount || defaultColCount;

  return current;
}

// We use these as placeholders to preserve spacing in grid mode
interface EmptyTile {
  mediaKey: string;
  isEmpty: true;
}
interface Tile {
  mediaKey: string;
  mediaStep: number;
  mediaIndex: number;
  runSignature: RunSignature;
  run: RunWithHistoryAndMediaWandb;
  controls?: MediaPanelCardControl;

  // We don't set this for regular tiles, but it make the typing cleaner
  // to interact with empty tiles to have an optional here
  isEmpty?: false;
}

// Helper function to get the current media key or a default
const defaultMediaKeys = (
  panelConfig: MediaBrowserPanelConfig,
  allMediaKeys: string[]
) => {
  return allMediaKeys.length > 0 ? [allMediaKeys[0]] : undefined;
};

type TileWithoutRunSig = Omit<Tile, 'runSignature'>;

interface Tiles {
  tiles: GroupedList<Tile | EmptyTile>;
  galleryAxisData?: number[] | null;
  gridAxisData?: {
    x: number[];
    y: number[];
  } | null;
  // Maintain full scope x axis data so we can preserve zoom
  xAxisTicks?: number[];
  allXAxisValues?: number[];
  historySampleRate?: number;
}

// We can merge this with Tiles on a rewrite
interface Layout {
  tiles: GroupedList<TileWithoutRunSig | EmptyTile>;
  galleryAxisData?: number[] | null;
  gridAxisData?: {
    x: number[];
    y: number[];
  } | null;
  // Maintain full scope x axis data so we can preserve zoom
  xAxisTicks?: number[];
  allXAxisValues?: number[];
  historySampleRate?: number;
}

interface StepMeta {
  runs: Array<{
    min: number;
    max: number;
    steps: number[];
  }>;
  steps: number[];
  min: number;
  max: number;
}

interface LayoutOptions {
  galleryPage?: {
    start: number;
    size: number;
  };
}

//  Helpers function for creating the tile layout
const makeLayout = (
  data: Data,
  runsWithKey: RunWithHistoryAndMediaWandb[],
  stepMeta: StepMeta,
  config: RequireSome<MediaConfigWithDefaults, 'columnCount' | 'mediaKeys'>,
  allMasks: {
    [mediaKey: string]: {
      [maskKey: string]: ClassLabelNode;
    };
  },
  layoutOptions: LayoutOptions,
  currentStep: number
): Layout | null => {
  const tiles: Layout['tiles'] = [];
  const {mediaKeys, gallerySettings, gridSettings, mode} = config;
  // NOTE: In future to make these reusable remove the null checks
  // move them to the parent and add the constraints to the types
  if (mediaKeys == null || mediaKeys === []) {
    return {tiles: []};
  }
  // NOTE: In future to make these reusable remove the null checks
  // move them to the parent and add the constraints to the types
  if (stepMeta.runs.length === 0) {
    return {tiles: []};
  }

  // This is some metadata we pass out of the
  // getAxis function for the UI
  // This is a little hacky we could do it cleaner
  let historySampleRate: number | undefined;

  // Give the step, index, and run, construct a single tile
  // or a group of tiles for the given set of constraints
  //
  // Constraints included: Media Keys
  //                       Tiling Layouts
  const addGroupOrTileForMediaKeys = (
    newTile: Omit<TileWithoutRunSig, 'mediaKey'>
  ) => {
    // Create a single tile or a group of tiles depending
    // the number of media keys set
    if (mediaKeys.length === 1) {
      const tile = {
        mediaKey: mediaKeys[0],
        ...newTile,
      };
      tiles.push(tile);
    } else {
      for (const mediaKey of config.mediaKeys) {
        const group = [];
        const tile = {
          mediaKey,
          ...newTile,
        };
        group.push(tile);
        tiles.push({groupType: 'mediaKeys', items: group});
      }
    }
    return tiles[tiles.length - 1];
  };
  // It returns a range of indices to be used as a lens on
  // the parent collection
  const getAxisData = (
    axis: Axis,
    // Min and max indexes to zoom on
    opts?: {
      maxSize?: number;
      min?: number;
      max?: number;
    }
  ) => {
    const maxSize = opts?.maxSize;

    if (axis === 'step') {
      const min = opts?.min ?? -Infinity;
      const max = opts?.max ?? Infinity;
      const stepsInRange = stepMeta.steps.filter(s => s >= min && s <= max);
      const samples = maxSize
        ? Math.min(stepsInRange.length, maxSize)
        : stepsInRange.length;
      const stepData = maxSize
        ? sampleSorted(stepsInRange, samples)
        : stepsInRange;

      historySampleRate =
        (stepsInRange[stepsInRange.length - 1] - stepsInRange[0] - 2) / samples;

      return stepData;
    } else if (axis === 'run') {
      // For runs don't sample, take first n
      return _.range(0, runsWithKey.slice(0, maxSize).length);
    } else if (axis === 'index') {
      // Get the length of the media array
      const metadata =
        runsWithKey[0].history &&
        getFirstMediaMetadata(
          runsWithKey[0].history,
          mediaKeys[0],
          currentStep
        );

      const mediaGrouping = (metadata && metadata.grouping) || 1;
      const mediaCount = ((metadata && metadata.count) || 1) / mediaGrouping;
      const mediaIndices = _.range(0, mediaCount);

      const res =
        maxSize && mediaCount > maxSize
          ? _.take(mediaIndices, maxSize)
          : mediaIndices;

      return res;
    } else if (axis === 'camera') {
      return _.range(0, 20);
    } else {
      // Note: This shouldn't be reached, but it is needed
      // to provide hints to the type system.
      throw new Error('Unsupported axis key selected');
    }
  };
  let galleryAxisData: number[] | null = null;
  let gridAxisData: {
    x: number[];
    y: number[];
  } | null = null;

  // We want to return the full axis as well
  // So we can render the full axis and the zoomed
  // on subset
  let xAxisTicks: number[] | undefined;
  let allXAxisValues: number[] | undefined;

  // Create a list of tiles for gallery mode
  if (
    mode === 'gallery' &&
    gallerySettings != null &&
    gallerySettings.axis != null
  ) {
    const axis = gallerySettings.axis;
    galleryAxisData = getAxisData(axis, {
      maxSize: config.maxGalleryItems,
    });

    const axisLength = galleryAxisData.length;

    for (let i = 0; i < axisLength; i++) {
      const runIndex = axis === 'run' ? i : 0;
      const run = runsWithKey[runIndex];
      // The step should be the currently set step
      // or in the case of unset panels the max step
      const defaultStep = stepMeta.runs[runIndex].max;
      const panelStep =
        config.stepIndex != null ? config.stepIndex : defaultStep;
      const mediaStep = axis === 'step' ? galleryAxisData[i] : panelStep;
      const mediaIndex =
        axis === 'index'
          ? (galleryAxisData[i] as number)
          : config.mediaIndex || 0;
      const notes = data.filteredRunsById[run.name].notes;

      const meta =
        axis === 'camera'
          ? {camera: {cameraIndex: galleryAxisData[i]}}
          : undefined;

      const tile = {
        mediaStep,
        mediaIndex,
        run: {...run, history: run.history || [], notes},
        meta,
      };
      addGroupOrTileForMediaKeys(tile);
    }
    // Start Grid Mode Setup
  } else if (
    mode === 'grid' &&
    gridSettings != null &&
    gridSettings.xAxis != null &&
    gridSettings.yAxis != null
  ) {
    const {xAxis, yAxis} = gridSettings;

    // Full Axis Data for preserving the non-zoom version
    xAxisTicks = getAxisData(xAxis, {
      maxSize: config.columnCount,
    });

    allXAxisValues = getAxisData(xAxis);

    // Actual Axis Data(Including Zoom)
    const [minX, maxX] = [
      _.get(config.selection?.xAxis, 0),
      _.get(config.selection?.xAxis, 1),
    ];
    const [minY, maxY] = [
      _.get(config.selection?.yAxis, 0),
      _.get(config.selection?.yAxis, 1),
    ];
    const groupSize = mediaKeys.length;

    const xAxisData = getAxisData(xAxis, {
      maxSize: config.columnCount / groupSize,
      min: minX,
      max: maxX,
    });
    const yAxisData = getAxisData(yAxis, {
      maxSize: config.maxYAxisCount,
      min: minY,
      max: maxY,
    });
    gridAxisData = {x: xAxisData, y: yAxisData};

    // Each group is composed of 1 tile for each mediaKey
    // Or multiple if it has many mask tiles
    const groupCount = calcGroupCount(config);

    for (let row = 0; row < yAxisData.length; row++) {
      const addedToRow = [];
      for (let col = 0; col < config.columnCount / groupCount; col++) {
        // we add empty tiles to fill the row if the number of tiles < columnCount
        if (col >= xAxisData.length) {
          let totalTiles = 0;
          addedToRow.forEach(tileOrGroup => {
            const ts =
              'items' in tileOrGroup ? tileOrGroup.items : [tileOrGroup];
            ts.forEach(t => {
              const maskOptions = getMaskOptions(config, allMasks, t as Tile);
              totalTiles += maskOptions.length;
            });
          });
          while (totalTiles < config.columnCount) {
            tiles.push({
              mediaKey: config.mediaKeys[0],
              isEmpty: true,
            } as EmptyTile);
            totalTiles++;
          }
          break;
        }

        const runIndex = xAxis === 'run' ? col : yAxis === 'run' ? row : 0;

        const run = runsWithKey[runIndex];

        const defaultStep = stepMeta.runs[runIndex].max;
        const mediaStep =
          xAxis === 'step'
            ? xAxisData[col]
            : yAxis === 'step'
            ? yAxisData[row]
            : config.stepIndex || defaultStep;

        const mediaIndex =
          xAxis === 'index'
            ? (xAxisData[col] as number)
            : yAxis === 'index'
            ? yAxisData[row]
            : config.mediaIndex || 0;

        const notes = data.filteredRunsById[run.name].notes;

        const tile = {
          mediaStep,
          mediaIndex,
          run: {...run, history: run.history || [], notes},
        };

        addedToRow.push(addGroupOrTileForMediaKeys(tile));
      }
    }
  } else {
    // NOTE: This shouldn't be reached, but is needed as a hint to the type system.
    throw new Error('Unsupported layout mode');
  }

  return {
    tiles,
    gridAxisData,
    galleryAxisData,
    xAxisTicks,
    allXAxisValues,
    historySampleRate,
  };
};

export default class MediaBrowserPanel extends React.Component<
  MediaBrowserPanelProps,
  MediaBrowserPanelState
> {
  static defaults = {gridWidth: 12};

  static transformQuery(
    query: Query.Query,
    config: MediaBrowserPanelConfig
  ): RunsDataQuery {
    const mediaKeys = config.mediaKeys || undefined;
    const result = toRunsDataQuery(
      query,
      {
        selectionsAsFilters: true,
      },
      {historyKeyInfo: true}
    );
    result.disabled = false;
    result.history = true;
    // This is required to get _wandb for single runs
    result.wandbKeys = [WANDB_MASK_CLASS_LABEL_KEY, WANDB_BBOX_CLASS_LABEL_KEY];
    //  This is required to get _wandb for multi runs
    // result.configKeys = ['_wandb'];
    if (mediaKeys) {
      result.historySpecs = [
        {keys: ['_step', ...mediaKeys], samples: CHART_SAMPLES},
      ];
    }
    result.page = {
      size: 30,
    };

    if (isGrouped(query)) {
      // We need the metadata for grouping because we do it locally
      // TODO: move grouping to server
      // result.disableMeta = false;

      // Disable grouping for this query, we'll do it ourselves.
      result.queries = result.queries.map(q => ({...q, grouping: []}));
    }

    return result;
  }

  render() {
    return <MediaBrowserPanelFunction {...this.props} />;
  }
}

const configWithDefaults = (
  config: MediaBrowserPanelConfig,
  mediaType: MediaCardType | null,
  inRunPage: boolean,
  mediaCount?: number
) => {
  // Set sane defaults
  const gallerySettings =
    config.gallerySettings ??
    (inRunPage
      ? {
          axis: 'index' as Axis,
        }
      : {
          axis: 'run' as Axis,
        });

  const gridSettings =
    config.gridSettings ??
    (inRunPage
      ? {
          xAxis: 'step' as Axis,
          yAxis: 'index' as Axis,
        }
      : {xAxis: 'step' as Axis, yAxis: 'run' as Axis});

  const mode = config.mode ?? 'gallery';
  const pixelated = config.pixelated ?? true;

  // Actual Size and Fit to 1 Dimension is currently restricted to gallery mode
  let actualSize;
  let fitToDimension;
  if (mode === 'grid') {
    actualSize = false;
    fitToDimension = false;
  } else {
    actualSize = config.actualSize ?? false;
    fitToDimension = config.fitToDimension ?? false;
  }

  const columnCount = defaultColumnCount(
    mediaType,
    config.columnCount,
    mediaCount
  );

  const maxYAxisCount = config.maxYAxisCount ?? DEFAULT_MAX_Y_AXIS_COUNT;

  return {
    ...config,
    actualSize,
    fitToDimension,
    columnCount,
    mode,
    pixelated,
    gallerySettings,
    gridSettings,
    maxYAxisCount,
  };
};

// A type for the config that contains defaults.
// Allows for cleaner type logic
type MediaConfigWithDefaults = ReturnType<typeof configWithDefaults>;

const MediaBrowserPanelFunction = makeComp(
  (props: MediaBrowserPanelProps) => {
    const [mediaBrowserWidth, setMediaBrowserWidth] = useState<number>();
    const [mediaBrowserHeight, setMediaBrowserHeight] = useState<number>();
    const mediaBrowserWidthRef = useRef(mediaBrowserWidth);
    mediaBrowserWidthRef.current = mediaBrowserWidth;
    const mediaBrowserHeightRef = useRef(mediaBrowserHeight);
    mediaBrowserHeightRef.current = mediaBrowserHeight;
    const mediaBrowserRef = useRef<HTMLDivElement | null>();

    // Update the image browser width, to determine if we need to resize the media components
    // fires when window is resized or componentDidUpdate
    const updateMediaBrowserSize = useCallback(
      _.debounce(() => {
        const currentMediaBrowserWidth =
          mediaBrowserRef.current && mediaBrowserRef.current.clientWidth;

        const currentMediaBrowserHeight =
          mediaBrowserRef.current && mediaBrowserRef.current.clientHeight;

        if (
          currentMediaBrowserWidth != null &&
          mediaBrowserWidthRef.current !== currentMediaBrowserWidth
        ) {
          setMediaBrowserWidth(currentMediaBrowserWidth);
        }

        if (
          currentMediaBrowserHeight != null &&
          mediaBrowserHeightRef.current !== currentMediaBrowserHeight
        ) {
          setMediaBrowserHeight(currentMediaBrowserHeight);
        }
      }, 100),
      []
    );

    // This allows us to set the ref and perform an operation on ref update
    const mediaBrowserRefCallback = useCallback(
      (ele: HTMLDivElement) => {
        mediaBrowserRef.current = ele;
        updateMediaBrowserSize();
      },
      [updateMediaBrowserSize]
    );

    useEffect(() => {
      window.addEventListener('resize', updateMediaBrowserSize);
      return () => {
        window.removeEventListener('resize', updateMediaBrowserSize);
      };
    }, [updateMediaBrowserSize]);

    /* MEDIA KEYS */
    const allMediaKeys = getMediaKeysFromKeyInfo(props.data.histories.keyInfo);
    const defaultKeys = defaultMediaKeys(props.config, allMediaKeys);
    const currentMediaKeys = props.config.mediaKeys;
    const {updateConfig} = props;

    React.useEffect(() => {
      // We have to set this in the global config instead of overriding the local
      // config so that the panel fetches the data
      if (currentMediaKeys == null) {
        updateConfig({mediaKeys: defaultKeys});
      }
    }, [currentMediaKeys, updateConfig, defaultKeys]);

    const panelViewerRef = useRef<HTMLDivElement>(null);

    const keyTypes = RunHelpers.keyTypes(props.data.histories.keyInfo);

    const firstMediaKey =
      (currentMediaKeys != null &&
        currentMediaKeys[0] != null &&
        currentMediaKeys[0]) ||
      null;

    const mediaType =
      firstMediaKey != null ? (keyTypes[firstMediaKey] as MediaString) : null;

    // The panel system shouldn't allow creation of this panel type
    // This check is to simplify the type checks
    if (mediaType === 'data-frame') {
      return (
        <PanelError
          message={'Media type: "dataframe" unsupported as a media card'}
        />
      );
    }

    if (mediaType === 'media') {
      return <PanelError message={'Unsupported media type'} />;
    }

    const {data} = props;

    const allRuns: RunWithHistoryAndMediaWandb[] = (
      (data && data.histories && data.histories.data) ||
      []
    ).map(r => {
      const run = data.filteredRunsById[r.name];
      const rsId = run.runsetInfo.id;
      const query = _.find(props.query.queries, q => q.id === rsId);
      if (query == null) {
        throw new Error(
          'Inconsistent data from the server, run does not have a matching runset'
        );
      }
      // props.;
      return {
        ...r,
        _wandb: run._wandb,
        entityName: query.entityName,
        projectName: query.projectName,
      };
    });

    const allMasks = classLabelMap(
      allRuns,
      props.config.mediaKeys ?? [],
      WANDB_MASK_CLASS_LABEL_KEY
    );

    const allBoxes = classLabelMap(
      allRuns,
      props.config.mediaKeys ?? [],
      WANDB_BBOX_CLASS_LABEL_KEY
    );

    // For now we memoize the function globally
    const [runsWithMedia, runsWithoutMedia] = _.partition(
      allRuns,
      runHistory => {
        // Check for runs that contains any of the chosen media keys
        const steps = memoizedStepsWithKeys(runHistory, currentMediaKeys);

        return steps.length > 0;
      }
    );

    const allStepData = runsWithMedia
      .map(runHistory =>
        memoizedStepsWithKeys(runHistory, currentMediaKeys).map(
          s => s._step as number
        )
      )
      .map(steps => ({min: _.min(steps) || 0, max: _.max(steps) || 0, steps}));

    const allMax = Math.max(...allStepData.map(sd => sd.max));
    const allMin = Math.min(...allStepData.map(sd => sd.min));
    const allSteps = new Set(_.flatten(allStepData.map(sd => sd.steps)));

    const stepMeta = {
      runs: allStepData,
      min: allMin,
      max: allMax,
      steps: Array.from(allSteps).sort((a, b) => a - b),
    };

    /* STEP INDEX */
    const maxStep = stepMeta.max;
    const minStep = stepMeta.min;

    const currentStep = _.isUndefined(props.config.stepIndex)
      ? maxStep
      : props.config.stepIndex;

    const simpleType = mediaType && keyToMediaCardType(mediaType);
    const inRunPage = props.data.histories.data.length === 1;
    const firstHistory: RunHistoryRow[] | null =
      props.data.histories.data[0] != null
        ? props.data.histories.data[0].history
        : null;

    const firstMediaMetadata =
      firstMediaKey != null && firstHistory != null
        ? getFirstMediaMetadata(firstHistory, firstMediaKey, currentStep)
        : null;

    const mediaCount = inRunPage
      ? (firstMediaMetadata && firstMediaMetadata.count) ?? 1
      : props.data.histories.data.length;

    const firstMaskCount =
      firstMediaKey != null && allMasks[firstMediaKey] != null
        ? Object.keys(allMasks[firstMediaKey]).length
        : 0;

    const withDefaults = {
      ...configWithDefaults(
        props.config,
        simpleType,
        inRunPage,
        mediaCount * (firstMaskCount + 1)
      ),
    };

    const settingsSubConfig = {...withDefaults, mediaKeys: currentMediaKeys};

    const sizingSettings = {
      columnCount: {
        min: 1,
        max: MAX_COL_COUNT,
        current: withDefaults.columnCount,
      },
    };

    const hasMasks = Object.keys(allMasks).length > 0;
    const hasBoxes = Object.keys(allBoxes).length > 0;
    useEffect(() => {
      window.analytics.track('Media Panel Rendered', {
        type: simpleType,
        hasBoxes,
        hasMasks,
      });
    }, [simpleType, hasBoxes, hasMasks]);

    // The loading spinner that shows when a media panel is loading
    const PanelMediaLoader = () => {
      return (
        <div className="panel-media" ref={mediaBrowserRefCallback}>
          <Loader active size="large" />
        </div>
      );
    };

    if (data.loading) {
      return <PanelMediaLoader />;
    }

    // Create a panel body function so we can render our errors within
    // the panel body and allow the panel to remain configurable
    // on all error states
    const panelBody = (panelBodyProps: {
      children: React.ReactNode;
      header?: React.ReactNode;
    }) => {
      return props.configMode ? (
        <div className="panel-media__config-container">
          <div className="panel-media__config-content">
            {panelBodyProps.header}
            <div className="panel-media" ref={mediaBrowserRefCallback}>
              <div ref={panelViewerRef} className="panel-viewer">
                {panelBodyProps.children}
              </div>
            </div>
          </div>
          <div className="panel-media__config-settings">
            <PanelSettings
              multiRun={!inRunPage}
              mediaType={mediaType && keyToMediaCardType(mediaType)}
              sizingSettings={sizingSettings}
              allMediaKeys={allMediaKeys}
              config={settingsSubConfig}
              updateConfig={updateConfig}
            />
          </div>
        </div>
      ) : (
        <div data-test="media-panel" className="panel-media__container">
          {panelBodyProps.header}
          <div className="panel-media" ref={mediaBrowserRefCallback}>
            <div className="panel-viewer">{panelBodyProps.children}</div>
          </div>
        </div>
      );
    };

    // An error that renders in the panel body so it is still editable
    const ErrorBody = ({message}: {message: React.ReactChild}) => {
      return panelBody({children: <PanelError message={message} />});
    };

    if (currentMediaKeys == null || currentMediaKeys.length === 0) {
      return panelBody({
        children: <PanelError message={'No Media Key selected'} />,
      });
    }

    const config = {
      ...withDefaults,
      mediaKeys: currentMediaKeys,
    };

    if (mediaType == null) {
      return (
        <ErrorBody
          message={`Select runs that logged ${currentMediaKeys[0]} to visualize data here.`}
        />
      );
    }

    const runSignaturePartial = {
      entityName: data.entityName,
      projectName: data.projectName,
    };

    /* MEDIA METADATA */
    // get all (not-null) mediaMetadata from all runs
    const flattenedMediaMetadataForAllRuns: MediaCardMetadata[] = _.flatten(
      runsWithMedia.map((r: RunWithHistoryAndMediaWandb) =>
        _.compact(
          _.flatten(
            _.map(r.history, h => {
              return _.map(currentMediaKeys, k => h[k]);
            })
          )
        )
      )
    );

    // Plotly cards perform poorly past 8 on a page. This value allows us to perform well
    // with the default 2 panels in a section open
    const opts = {
      galleryPage: simpleType === 'plotly' ? {start: 0, size: 4} : undefined,
    };

    const layout = makeLayout(
      data,
      runsWithMedia,
      stepMeta,
      config,
      allMasks,
      opts,
      currentStep
    );

    // NOTE: This could be moved above make layout as a type check
    if (layout == null) {
      return <ErrorBody message={'Panel Needs Configuration'} />;
    }

    // Add the correct run metadata to each tile.
    const mediaTiles: GroupedList<Tile | EmptyTile> = mapGroupedLeafs(
      layout.tiles,
      t =>
        t.isEmpty === true
          ? t
          : {
              ...t,
              runSignature: {
                ...runSignaturePartial,
                entityName: t.run.entityName,
                projectName: t.run.projectName,
                runName: t.run.name,
              },
            }
    );

    // Grouping is a secret legacy setting that can be passed in via the CLI
    //
    // if grouping is > 1, we'll group the images accordingly
    // e.g. if grouping = 3, we'll show every 3 images as if they're one image
    const groupingCount = firstMediaMetadata?.grouping ?? 1;

    if (flattenedMediaMetadataForAllRuns == null) {
      return (
        <ErrorBody
          message={`Select runs that logged ${simpleType} with the key ${currentMediaKeys[0]}
        to visualize data here.`}
        />
      );
    }

    /* MEDIA INDEX */
    const counts = _.map(flattenedMediaMetadataForAllRuns, m => m.count ?? 1);
    const maxMedia = (_.max(counts) || 0) / groupingCount - 1;

    const minMediaIndex = 0;

    let currentMediaIndex = config.mediaIndex || 0;

    // this can happen if you switch to a mediaKey/step with a lower maxMediaIndex than the current mediaKey/step
    if (currentMediaIndex > maxMedia) {
      currentMediaIndex = maxMedia;
    }

    // scaleWaveformWidth: only used for audio
    // if true, the length of each displayed waveform will be scaled relative
    // to the width of the longest displayed waveform
    // const scaleWaveformWidth = _.isUndefined(panelConfig.scaleWaveformWidth)
    //   ? true
    //   : panelConfig.scaleWaveformWidth;
    // NOTE: Scale waveform currently has a  lot of bugs around
    // missing media keys. It needs to get worked to deal with missing media.
    // I'm disabling it for now.
    // const scaleWaveformWidth = false;

    if (_.isEmpty(data.filtered) || mediaType == null) {
      return (
        <ErrorBody
          message={`Select runs that logged ${simpleType} with the key ${currentMediaKeys[0]}
        to visualize data here.`}
        />
      );
    }

    if (mediaBrowserWidth == null || mediaBrowserHeight == null) {
      return <PanelMediaLoader />;
    }

    // If no simple type is returned from our mediaType we are rendering
    // a media panel for a non-media key type
    if (mediaType != null && simpleType == null) {
      return (
        <ErrorBody
          message={
            <div>
              <span>
                Selected runs are not logging media for the key:
                <b> {currentMediaKeys[0]}</b>, but instead are logging type{' '}
                <b>{simpleType}</b>
              </span>
            </div>
          }
        />
      );
    } else if (simpleType == null) {
      return (
        <ErrorBody
          message={`Select runs${mediaType ? ' that logged ' + mediaType : ''}${
            currentMediaKeys ? ` with the key "${currentMediaKeys[0]}"` : ''
          } to visualize data here.`}
        />
      );
    }

    if (sizingSettings == null) {
      return <PanelMediaLoader />;
    }

    const galleryAxes = [config.gallerySettings.axis];

    const gridAxes = [config.gridSettings.xAxis, config.gridSettings.yAxis];

    const activeAxes = config.mode === 'gallery' ? galleryAxes : gridAxes;

    // In gallery mode we want to label everything by what we are iterating across
    let labels: Axis[] = [];
    if (config.mode === 'gallery' && galleryAxes[0] !== 'index') {
      labels = labels.concat(galleryAxes);
    } else if (gridAxes[0] === 'run') {
      labels.push('run');
    }

    const stepSlider = {
      current: currentStep,
      min: minStep,
      max: maxStep,
      active: !_.includes(activeAxes, 'step'),
    };

    const indexSlider = {
      current: props.config.mediaIndex || 0,
      min: minMediaIndex,
      max: maxMedia,
      active: !_.includes(activeAxes, 'index'),
    };

    const paginationActive =
      opts.galleryPage != null &&
      config.mode === 'gallery' &&
      opts.galleryPage.size < mediaTiles.length;

    const mediaBrowserSize = {
      width: mediaBrowserWidth,
      height: mediaBrowserHeight - (paginationActive ? 30 : 0),
    };

    const contentProps = {
      labels,
      mediaType: simpleType,
      mediaBrowserSize,
      sizingSettings,
      layout: {
        ...layout,
        tiles: mediaTiles,
      },
      currentMediaKeys,
      config: _.omit(config, 'chartTitle'),
      updateConfig,
      panelRef: (props as any).panelRef,
      disableRunLinks: props.disableRunLinks,
      allMasks,
      opts,
      currentStep,
    };

    // Render the runs that are selected, but are hidden from the user
    const MissingRuns = () => {
      return runsWithoutMedia.length > 0 ? (
        <div style={{marginTop: 15, marginBottom: 35}}>
          <Popup
            hoverable
            trigger={
              <a href="#hover">
                Selected Runs Without Media ({`${runsWithoutMedia.length})`}
              </a>
            }>
            <List
              items={runsWithoutMedia.map(r => {
                const linkData = {
                  ...runSignaturePartial,
                  name: r.name,
                };
                return (
                  <a href={urls.run(linkData)}>
                    <li> {r.displayName}</li>
                  </a>
                );
              })}
            />
          </Popup>
        </div>
      ) : (
        <div> </div>
      );
    };

    const Pagination = (pageProps: {
      pageOpts: Required<LayoutOptions>['galleryPage'];
    }) => {
      const start = config.page?.start ?? 0;
      const size = pageProps.pageOpts.size;
      const total = mediaTiles.length;

      const nextDisabled: boolean = start + size > total;
      const prevDisabled: boolean = start === 0;

      const pageStart = start + 1;
      const pageEnd = Math.min(start + size, total);

      return (
        <div style={{marginBottom: -5}} className="inline-pagination">
          <div className="pagination-count">
            {pageStart} - {pageEnd} of {total}
          </div>
          <Button.Group className="pagination-buttons">
            <Button
              size="tiny"
              disabled={prevDisabled}
              className="wb-icon-button only-icon page-down-button"
              onClick={() => {
                updateConfig({
                  page: {start: start - size},
                });
              }}>
              <LegacyWBIcon name="previous"></LegacyWBIcon>
            </Button>
            <Button
              size="tiny"
              disabled={nextDisabled}
              className="wb-icon-button only-icon page-down-button"
              onClick={() => {
                updateConfig({
                  page: {start: start + pageProps.pageOpts.size},
                });
                if (panelViewerRef.current) {
                  panelViewerRef.current.scrollTop = 0;
                }
              }}>
              <LegacyWBIcon name="next"></LegacyWBIcon>
            </Button>
          </Button.Group>
        </div>
      );
    };

    const headerSubConfig = {
      ...config,
      chartTitle:
        config.chartTitle != null
          ? config.chartTitle
          : keysToTitle(currentMediaKeys),
    };

    return panelBody({
      header: (
        <PanelHeader
          {...{
            mediaType,
            mediaBrowserSize,
            sizingSettings,
            config: headerSubConfig,
            updateConfig,
            sliders: {step: stepSlider, index: indexSlider},
            stepMeta,
            panelRef: (props as any).panelRef,
            allMasks,
            allBoxes,
          }}
        />
      ),
      children: (
        <>
          {paginationActive && <Pagination pageOpts={opts.galleryPage!} />}
          <PanelContent {...contentProps} />
          <MissingRuns />
        </>
      ),
    });
  },
  {id: 'MediaBrowserPanelFunction'}
);

interface MediaBrowserSize {
  width: number;
  height: number;
}

const PanelHeader = makeComp(
  (props: {
    config: RequireSome<MediaBrowserPanelConfig, 'chartTitle'>;
    mediaBrowserSize: MediaBrowserSize;
    updateConfig: (config: Partial<MediaBrowserPanelConfig>) => void;
    sizingSettings: SizingSettings;
    sliders: {
      step: RangeSettings;
      index: RangeSettings;
    };
    stepMeta: StepMeta;
    panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
    allMasks: {
      [mediaKey: string]: {
        [maskKey: string]: ClassLabelNode;
      };
    };
    allBoxes: {
      [mediaKey: string]: {
        [boxKey: string]: ClassLabelNode;
      };
    };
  }) => {
    const minStep = props.sliders.step.min;
    const maxStep = props.sliders.step.max;
    const currentStepIndex = props.sliders.step.current;

    const maxMedia = props.sliders.index.max;
    const currentMediaIndex = props.sliders.index.current;

    const {updateConfig} = props;
    const {chartTitle} = props.config;
    const updateStep = useCallback(
      (value: number) =>
        // When panning to the last step, set it back to auto-update
        updateConfig({
          stepIndex: value === maxStep ? undefined : value,
        }),
      [maxStep, updateConfig]
    );

    const stepSliderActive =
      props.sliders.step.active && maxStep > 0 && minStep < maxStep;

    const indexSliderActive = props.sliders.index.active && maxMedia > 0;

    const bBoxThresholdSliderActive = Object.keys(props.allBoxes).length > 0;

    const controlSlidersActive =
      stepSliderActive || indexSliderActive || bBoxThresholdSliderActive;

    const boxData = useSelector(s => {
      return s.media.boxData[props.panelRef.id] ?? {};
    });

    const sliderRanges = useMemo(() => {
      const scoreRanges = {} as {
        [key: string]: {
          min: number;
          max: number;
        };
      };

      // Loop through all the relevant boxes and their scores
      // Constructing a range value for each set
      for (const k of props.config.mediaKeys ?? []) {
        _.forEach(boxData[k], (boxes, mediaID) => {
          boxes.forEach(v => {
            _.forEach(v.scores, (score, name) => {
              const oldRange = scoreRanges[name];
              let newRange;
              if (oldRange == null) {
                newRange = {min: score, max: score};
              } else {
                newRange = {
                  min: Math.min(oldRange.min, score),
                  max: Math.max(oldRange.max, score),
                };
              }
              scoreRanges[name] = newRange;
            });
          });
        });
      }

      return scoreRanges;
    }, [boxData, props.config.mediaKeys]);

    const bBoxSlider = () => {
      const bBoxSliderKeys = Object.keys(sliderRanges);

      if (bBoxSliderKeys.length === 0) {
        return;
      }

      const bBoxConfig = props.config.boundingBoxConfig ?? {};
      return (
        <div className="control-popup__item">
          {bBoxSliderKeys.map(k => {
            const currentMin = parseFloat(sliderRanges[k].min.toPrecision(3));
            const currentMax = parseFloat(sliderRanges[k].max.toPrecision(3));

            const currentSliderControl: BoundingBoxSliderControl = _.get(
              props.config.boundingBoxConfig,
              ['sliders', k],
              {
                comparator: 'gte',
                value: currentMin,
              }
            );

            const updateSlider = (
              newControl: Partial<BoundingBoxSliderControl>
            ) => {
              const newConfig = produce(bBoxConfig, draft => {
                _.set(draft, ['sliders', k], {
                  ...currentSliderControl,
                  ...newControl,
                });
              });
              updateConfig({boundingBoxConfig: newConfig});
            };

            return (
              <div className="confidence-slider__container" key={k}>
                <Checkbox
                  className="confidence-slider__toggle"
                  checked={!currentSliderControl.disabled}
                  onChange={(e, value) => {
                    // Only toggle if all fields are set.
                    const newDisabled = !currentSliderControl.disabled;
                    updateSlider({disabled: newDisabled});
                  }}></Checkbox>
                <div className={'confidence-slider__key'}>{k}</div>
                <Dropdown
                  className={'confidence-slider__op'}
                  inline
                  options={[
                    {
                      text: <span className="symbol">≥</span>,
                      value: 'gte',
                      key: 'gte',
                    },
                    {
                      text: <span className="symbol">≤</span>,
                      value: 'lte',
                      key: 'lte',
                    },
                  ]}
                  onChange={(e, {value}) => {
                    const key = value as CompareOp;
                    updateSlider({comparator: key});
                  }}
                  value={currentSliderControl.comparator}
                />
                <SliderInput
                  className={
                    'confidence-slider__value panel-media__class-slider'
                  }
                  sliderInPopup
                  min={currentMin}
                  max={currentMax}
                  minLabel={currentMin.toString()}
                  maxLabel={currentMax.toString()}
                  step={(currentMax - currentMin) / 100}
                  value={currentSliderControl.value}
                  hasInput
                  onChange={v => {
                    updateSlider({value: parseFloat(v.toPrecision(3))});
                  }}
                />
              </div>
            );
          })}
        </div>
      );
    };

    const controlSliders = (stepSliderActive || indexSliderActive) && (
      <>
        <div className={'control-popup__slider-container control-popup__item'}>
          {stepSliderActive && (
            <div className="control-popup__control-slider">
              <Popup
                trigger={<label>Step</label>}
                content={
                  'This increments every time you call wandb.log in your script'
                }></Popup>
              <SliderInput
                sliderInPopup
                min={minStep}
                max={maxStep}
                minLabel={minStep.toString()}
                maxLabel={maxStep.toString()}
                step={1}
                ticks={props.stepMeta.steps}
                value={currentStepIndex}
                strideLength={props.config.stepStrideLength}
                hasInput
                debounceTime={10}
                onChange={updateStep}
              />
            </div>
          )}

          {indexSliderActive && (
            <div className="control-popup__control-slider">
              <Popup
                trigger={<label>Index</label>}
                content={
                  'This is the index in the array of examples in your wandb.log call'
                }></Popup>
              <SliderInput
                sliderInPopup
                min={0}
                max={maxMedia}
                minLabel="0"
                maxLabel={maxMedia.toString()}
                step={1}
                value={currentMediaIndex}
                hasInput
                onChange={configValue =>
                  updateConfig({mediaIndex: configValue})
                }
              />
            </div>
          )}
        </div>
      </>
    );

    const [maskSearchQuery, setMaskSearchQuery] = useState<string>('');
    const maskQueryRegex = React.useMemo(() => {
      return fuzzyMatchRegex(maskSearchQuery);
    }, [maskSearchQuery]);

    const [boxSearchQuery, setBoxSearchQuery] = useState<string>('');
    const boxQueryRegex = React.useMemo(() => {
      return fuzzyMatchRegex(boxSearchQuery);
    }, [boxSearchQuery]);

    const maskConfig = props.config.segmentationMaskConfig ?? {};
    const boundingBoxConfig = props.config.boundingBoxConfig ?? {};

    const maskControlsActive = Object.keys(props.allMasks).length !== 0;

    const maskLayoutToggle = (mediaKey: string) => {
      const allMaskKeys = Object.keys(props.allMasks[mediaKey]);
      const setToggle = (type: LayoutType) => {
        const newTileLayout = produce(props.config.tileLayout ?? {}, draft => {
          const maskOptions = maskOptionsForLayout(type, allMaskKeys ?? []);

          draft[mediaKey] = {type, maskOptions};
          return draft;
        });
        updateConfig({tileLayout: newTileLayout});
      };

      const layoutType = _.get(
        props.config.tileLayout,
        [mediaKey, 'type'],
        DEFAULT_LAYOUT_TYPE
      );

      return (
        <ButtonGroup>
          <Button
            size="tiny"
            icon
            className={classNames({
              'action-button--active': layoutType === 'ALL_STACKED',
            })}
            onClick={() => setToggle('ALL_STACKED')}>
            <LegacyWBIcon name="overlay-stack"></LegacyWBIcon>
          </Button>
          <Button
            size="tiny"
            icon
            className={classNames({
              'action-button--active': layoutType === 'MASKS_NEXT_TO_IMAGE',
            })}
            onClick={() => setToggle('MASKS_NEXT_TO_IMAGE')}>
            <LegacyWBIcon name={'overlay-2-column'}></LegacyWBIcon>
          </Button>
          {allMaskKeys.length > 1 && (
            <Button
              size="tiny"
              icon
              className={classNames({
                'action-button--active': layoutType === 'ALL_SPLIT',
              })}
              onClick={() => setToggle('ALL_SPLIT')}>
              <LegacyWBIcon name="overlay-3-column"></LegacyWBIcon>
            </Button>
          )}
        </ButtonGroup>
      );
    };

    const isImageShownForMask = (
      mediaKey: string,
      maskKey: string
    ): boolean => {
      const layout = _.get(props.config, ['tileLayout', mediaKey], {
        maskOptions: [],
      }) as {
        maskOptions: MaskOptions[];
      };

      const mo = _.find(layout.maskOptions, v =>
        _.includes(v.maskKeys, maskKey)
      );

      return mo?.showImage ?? false;
    };

    const toggleImageForMask = (mediaKey: string, maskKey: string) => {
      const allLayouts = props.config.tileLayout ?? {};
      const isShown = isImageShownForMask(mediaKey, maskKey);

      const newLayout = produce(allLayouts, draft => {
        // We need to set a default layout to perform updates
        if (draft[mediaKey] == null) {
          const allMaskKeys = Object.keys(props.allMasks[mediaKey]);
          draft[mediaKey] = defaultLayout(allMaskKeys);
        }
        const layout = draft[mediaKey];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < layout.maskOptions.length; i++) {
          const maskKeys = layout.maskOptions[i].maskKeys;

          if (_.includes(maskKeys, maskKey)) {
            layout.maskOptions[i].showImage = !isShown;
          }
        }
      });

      updateConfig({tileLayout: newLayout});
    };

    const maskControls = (
      <div key="mask-control">
        {Object.keys(props.allMasks).map(mediaKey => {
          const relevantMasks = props.allMasks[mediaKey];
          return (
            <React.Fragment key={mediaKey}>
              <div
                style={{marginTop: 15}}
                className="mask-control__mask-section">
                {maskLayoutToggle(mediaKey)}
                <span style={{marginLeft: -5}}>
                  <HelpPopup helpText="Toggle the layout of the masks between: a stack of image and masks, masks adjacent to image, and all spread out"></HelpPopup>
                </span>
              </div>
              {Object.keys(relevantMasks).map(maskKey => {
                const classDict = props.allMasks[mediaKey][maskKey].value;
                return (
                  <div className="mask-control__mask-section" key={maskKey}>
                    <MaskControlComponent
                      mediaKey={mediaKey}
                      maskKey={maskKey}
                      classID={'all'}
                      className="All"
                      maskConfig={maskConfig}
                      updateConfig={updateConfig}
                    />
                    <span
                      style={{
                        fontWeight: 'bold',
                        marginTop: 15,
                      }}>{`${maskKey}`}</span>
                    <LegacyWBIcon
                      style={{
                        marginLeft: 5,
                        fontSize: '1.1em',
                        cursor: 'pointer',
                        verticalAlign: 'middle',
                        color: isImageShownForMask(mediaKey, maskKey)
                          ? 'black'
                          : 'grey',
                      }}
                      onClick={() => toggleImageForMask(mediaKey, maskKey)}
                      name="panel-images"></LegacyWBIcon>
                    <Input
                      className="mask-search__input"
                      icon={
                        <LegacyWBIcon
                          style={{cursor: 'pointer'}}
                          name="search"></LegacyWBIcon>
                      }
                      iconPosition="left"
                      value={maskSearchQuery}
                      onChange={(e, {value}) => {
                        setMaskSearchQuery(value);
                      }}
                    />
                    <ShowMoreContainer iconSize="big">
                      {Object.keys(classDict)
                        .map(k => [k, classDict[Number(k)]])
                        .filter(([k, name]) => name.match(maskQueryRegex))
                        .map(([k, name]) => (
                          <MaskControlComponent
                            key={mediaKey + maskKey + name}
                            mediaKey={mediaKey}
                            maskKey={maskKey}
                            classID={k}
                            className={name}
                            maskConfig={maskConfig}
                            updateConfig={updateConfig}
                          />
                        ))}
                    </ShowMoreContainer>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    );

    const boundingBoxControlsActive = Object.keys(props.allBoxes).length !== 0;
    const boundingBoxControls = (
      <div key="box-control">
        {Object.keys(props.allBoxes).map(mediaKey => {
          const relevantMasks = props.allBoxes[mediaKey];
          return (
            <React.Fragment key={mediaKey}>
              {Object.keys(relevantMasks).map(boxKey => {
                const classDict = props.allBoxes[mediaKey][boxKey].value;
                return (
                  <div className="mask-control__mask-section" key={boxKey}>
                    <BBoxControlComponent
                      mediaKey={mediaKey}
                      boxKey={boxKey}
                      classID={'all'}
                      className="All"
                      boxConfig={boundingBoxConfig}
                      updateConfig={updateConfig}
                    />
                    <span
                      style={{
                        fontWeight: 'bold',
                        marginTop: 15,
                      }}>{`${boxKey}`}</span>
                    <BBoxStyleControl
                      mediaKey={mediaKey}
                      boxKey={boxKey}
                      boxConfig={boundingBoxConfig}
                      updateConfig={updateConfig}
                    />

                    <Input
                      className="mask-search__input"
                      size="large"
                      icon={
                        <LegacyWBIcon
                          style={{cursor: 'pointer'}}
                          name="search"></LegacyWBIcon>
                      }
                      iconPosition="left"
                      value={boxSearchQuery}
                      onChange={(e, {value}) => {
                        setBoxSearchQuery(value);
                      }}
                    />

                    <ShowMoreContainer iconSize="big">
                      {Object.keys(classDict)
                        .map(k => [k, classDict[Number(k)]])
                        .filter(([k, name]) => name.match(boxQueryRegex))
                        .map(([k, name]) => (
                          <BBoxControlComponent
                            key={mediaKey + boxKey + name}
                            mediaKey={mediaKey}
                            boxKey={boxKey}
                            classID={k}
                            className={name}
                            boxConfig={boundingBoxConfig}
                            updateConfig={updateConfig}
                          />
                        ))}
                    </ShowMoreContainer>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    );

    const [popup, setPopup] = React.useState<boolean>();

    return (
      <div>
        <div className="panel-header media-panel-header">
          <h6
            style={{width: '100%'}}
            className="panel-title"
            title={chartTitle}>
            {chartTitle}
          </h6>
          {(controlSlidersActive ||
            maskControlsActive ||
            boundingBoxControlsActive) && (
            <Popup
              position={'top left'}
              basic
              pinned
              on="click"
              trigger={
                <LegacyWBIcon
                  style={{cursor: 'pointer', marginTop: -30}}
                  onClick={() => setPopup(!popup)}
                  className={classNames(
                    'media-popup-button',
                    popup
                      ? 'media-popup-button__open'
                      : 'media-popup-button__closed'
                  )}
                  name="configuration"
                />
              }
              popperDependencies={[Object.keys(props.allBoxes).length]}
              open={popup}
              onClose={e => {
                // Prevent the popup when operating a slider
                // The sliders lie out of the popup and trigger a close event
                //
                // This is a bit of a hack, I'd be happy to move on in the
                // future
                //
                // It must be done for all nested popups
                const nestedPopupSelector = [
                  INPUT_SLIDER_CLASS,
                  STYLE_POPUP_CLASS,
                ]
                  .map(c => '.' + c)
                  .join(', ');

                const inPopup =
                  (e.target as HTMLElement).closest(nestedPopupSelector) !=
                  null;

                if (inPopup) {
                  // Do nothing
                } else {
                  setPopup(false);
                }
              }}
              onOpen={e => {
                setPopup(true);
              }}
              content={
                <div className={classNames('control-popup__container')}>
                  {controlSliders}
                  {bBoxThresholdSliderActive && bBoxSlider()}
                  {boundingBoxControls}
                  {maskControls}
                </div>
              }
            />
          )}
        </div>
      </div>
    );
  },
  {id: 'PanelHeader'}
);

const PanelContent = makeComp(
  (props: {
    disableRunLinks?: boolean;
    mediaBrowserSize: {
      width: number;
      height: number;
    };
    config: Omit<MediaBrowserPanelConfig, 'chartTitle'>;
    updateConfig: (config: Partial<MediaBrowserPanelConfig>) => void;
    // NOTE:  These values are derived from config. We could move these out of
    // the argument and into helper function .e.g: getActiveAxes
    labels: Axis[];
    sizingSettings: SizingSettings;
    mediaType: MediaCardType;
    layout: Tiles;
    panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
    allMasks: {
      [mediaKey: string]: {
        [maskKey: string]: ClassLabelNode;
      };
    };
    opts: LayoutOptions;
    currentStep: number;
  }) => {
    const {
      mediaType,
      layout,
      mediaBrowserSize,
      config,
      disableRunLinks,
      currentStep,
    } = props;
    const mediaTiles = layout.tiles;

    let scale: number | undefined;

    const {columnCount} = props.sizingSettings;

    const padding = PADDING;

    const availableWidth =
      mediaBrowserSize.width - (columnCount.current + 1) * padding;
    const availableHeight = mediaBrowserSize.height - 2 * padding;

    const firstTile = firstGrouped(props.layout.tiles);

    const firstMediaMetadata =
      firstTile != null && !firstTile.isEmpty && firstTile.run.history
        ? getFirstMediaMetadata(
            firstTile.run.history,
            firstTile.mediaKey,
            currentStep
          )
        : null;

    if ((firstTile == null && firstTile) || firstMediaMetadata == null) {
      return (
        <PanelError
          message={
            <div>
              Selected runs do not have media for given keys:
              <br />
              {`${config.mediaKeys && config.mediaKeys.join(', ')}`}
            </div>
          }
        />
      );
    }

    // **** Sizing **** //

    const groupingCount = firstMediaMetadata?.grouping ?? 1;
    // Helper function for getting card size given the current media and panel settings
    const mediaWidth =
      firstMediaMetadata?.width && firstMediaMetadata.width * groupingCount;
    const mediaHeight = firstMediaMetadata.height;

    let cardWidth = availableWidth / props.sizingSettings.columnCount.current;
    let cardHeight = availableHeight;

    // Use width height values that fall back to the card size if we don't
    // have any metadata(This happened in old versions of the cli)
    const imgWidth = mediaWidth ?? cardWidth;
    const imgHeight = mediaHeight ?? cardHeight;

    if (mediaType === 'image') {
      if (config.actualSize) {
        cardWidth = mediaWidth ?? cardWidth;
        cardHeight = mediaHeight ?? cardHeight;
      } else if (config.fitToDimension) {
        if (imgWidth / availableWidth > imgHeight / availableHeight) {
          cardHeight = availableHeight;
          cardWidth = (cardHeight / imgHeight) * imgWidth;
        } else {
          cardWidth = availableWidth;
          cardHeight = (cardWidth / imgWidth) * imgHeight;
        }
      } else {
        // Size all cards by the column width and adjust the height
        // To maintain the aspect ratio
        cardHeight = (cardWidth / imgWidth) * imgHeight;
      }
    } else if (mediaType === 'html' || mediaType === 'plotly') {
      cardHeight = mediaBrowserSize.height;
    } else if (mediaType === 'object3D') {
      cardHeight = cardWidth;
    } else if (mediaType === 'audio') {
      cardHeight = 100;
      // Percent Scaling
      scale = 100;
    } else {
      cardHeight = cardWidth;
    }

    // If the tiles don't fit in the media panel we want to shrink them
    // and change the layout so they're centered
    let shrunk = false;
    const margins = 25;
    if (
      cardHeight > mediaBrowserSize.height - margins &&
      config.actualSize !== true &&
      config.fitToDimension !== true
    ) {
      const aspectRatio =
        (mediaWidth ?? cardWidth) / (mediaHeight ?? cardHeight);
      cardHeight = mediaBrowserSize.height - margins;
      cardWidth = (mediaBrowserSize.height - margins) * aspectRatio;
      shrunk = true;
    }

    // **** End Sizing **** //

    // Sampled x-Axis data
    const xAxisData = layout.gridAxisData?.x;

    // const yAxisData =
    //   layout.axisData instanceof Array ? null : layout.axisData.y;
    // const xAxisZoom = config.zoom?.xAxis;

    const allXAxisValues = layout.allXAxisValues;

    // Local pagination
    const start = props.config.page?.start ?? 0;
    const paginatedTiles =
      props.opts.galleryPage && props.config.mode === 'gallery'
        ? mediaTiles.slice(start, start + props.opts.galleryPage?.size)
        : mediaTiles;

    return (
      <div>
        {config.gridSettings?.xAxis === 'step' &&
          xAxisData &&
          layout.xAxisTicks != null &&
          allXAxisValues != null &&
          (config.columnCount ?? 1) > 1 && (
            <React.Fragment>
              <StaticAxis
                className={`panel-media__x-axis-top ${true &&
                  'panel-media__x-axis-top--revealed'}`}
                ticks={xAxisData}
                direction="top"
                itemWidth={cardWidth}
                width={mediaBrowserSize.width}
              />
              <SelectableAxis
                className="panel-media__x-axis"
                ticks={layout.xAxisTicks}
                direction="bottom"
                itemWidth={cardWidth}
                width={mediaBrowserSize.width}
                // Use axis select to indicate zoom
                selection={config.selection?.xAxis}
                onSelect={range => {
                  if (config.columnCount) {
                    const newRange = expandRangeToNElements(
                      range,
                      allXAxisValues,
                      config.columnCount
                    );
                    props.updateConfig({
                      selection: {...config.selection, xAxis: newRange},
                    });

                    return newRange;
                  } else {
                    return range;
                  }
                }}
              />
            </React.Fragment>
          )}

        <Card.Group
          style={{
            gridTemplateColumns: `repeat(${columnCount.current},${cardWidth}px)`,
            minWidth: cardWidth,
          }}
          className={
            'media-cards' +
            ' ' +
            (config.pixelated ? 'media-cards--pixelated' : '') +
            ' ' +
            (config.actualSize || config.fitToDimension
              ? 'media-cards--actual'
              : 'media-cards--full') +
            ' ' +
            (mediaType === 'table' ? 'media-cards--table' : '') +
            ' ' +
            (shrunk && 'media-cards--shrunk')
          }>
          {paginatedTiles.map((tileOrGroup, tileIndex) => {
            // const firstTileOfGroup =
            //   'groupType' in tileOrGroup ? tileOrGroup.items[0] : tileOrGroup;
            const mediaComponents = [];

            const emptyTile = (
              <div
                key={tileIndex}
                className="ui card"
                style={{
                  width: cardWidth,
                  height: cardHeight,
                }}
              />
            );

            if (tileOrGroup == null) {
              return emptyTile;
            }

            // Expands each tile into multiple based on the mask layout
            const expandTileLayout = (
              tiles: Array<EmptyTile | Tile>
            ): Array<(EmptyTile | Tile) & {
              maskOptions: MaskOptions;
            }> => {
              const ts = tiles.map(t => {
                const maskOptions = getMaskOptions(
                  props.config,
                  props.allMasks,
                  t
                );
                return maskOptions.map(mo => ({...t, maskOptions: mo}));
              });

              return _.flatten(ts);
            };

            const tilesToRender = expandTileLayout(
              'groupType' in tileOrGroup ? tileOrGroup.items : [tileOrGroup]
            );
            const firstTileOfGroup = _.find(tilesToRender);

            // Pull out some metadata from the first tile that is needed
            // for the row labels
            //
            // NOTE: This code assumes that the runs are grouped in the same run

            const cardLabels = props.labels;
            const MediaComponent = mediaTypeToComponent(mediaType);

            for (const tile of tilesToRender) {
              if (tile.isEmpty != null && tile.isEmpty) {
                mediaComponents.push(emptyTile);
                continue;
              }
              const {run} = tile;
              const runNotes = run.notes;
              const {mediaIndex, mediaKey, mediaStep} = tile;

              // Mix Panel level controls with per tile control
              const controls = {
                ...tile.controls,
                segmentationMaskControl: config.segmentationMaskConfig,
                boundingBoxControl: config.boundingBoxConfig,
              };

              mediaComponents.push(
                <MediaComponent
                  key={`card-${tileIndex}-${mediaKey}-${tile.maskOptions.maskKeys.toString()}`}
                  run={run}
                  runSignature={tile.runSignature}
                  runNotes={runNotes}
                  labels={cardLabels}
                  globalStep={mediaStep}
                  mediaKey={mediaKey}
                  mediaIndex={mediaIndex}
                  width={cardWidth}
                  height={cardHeight}
                  mediaWidth={mediaWidth ?? undefined}
                  mediaHeight={mediaHeight ?? undefined}
                  actualSize={!!config.actualSize}
                  scale={scale}
                  controls={controls}
                  mediaPanelRef={props.panelRef}
                  moleculeConfig={config.moleculeConfig}
                  disableRunLink={disableRunLinks}
                  maskOptions={tile.maskOptions}
                />
              );
            }

            // Group has no renderable Tiles
            if (
              firstTileOfGroup == null ||
              (firstTileOfGroup.isEmpty != null &&
                firstTileOfGroup.isEmpty === true)
            ) {
              return emptyTile;
            }

            const step = firstTileOfGroup?.mediaStep;

            const RowTitleLink = RunHelpers.runLink(
              firstTileOfGroup.runSignature,
              firstTileOfGroup.run.displayName,
              {
                className: 'hide-in-run-page',
                target: '_blank',
                rel: 'noopener noreferrer',
              }
            );

            const groupCount = calcGroupCount(config);

            return (
              <React.Fragment key={tileIndex}>
                {config.mode === 'grid' &&
                  config.gridSettings?.yAxis &&
                  config.columnCount &&
                  (tileIndex * groupCount) % config.columnCount === 0 &&
                  (config.gridSettings.yAxis === 'run'
                    ? gridRow(RowTitleLink)
                    : config.gridSettings.yAxis === 'step' && step
                    ? gridRow(`Step ${step}`)
                    : null)}

                {mediaComponents}
              </React.Fragment>
            );
          })}
        </Card.Group>
      </div>
    );
  },
  {id: 'PanelContent', memo: true}
);

const PanelSettings = makeComp(
  (props: {
    config: RequireSome<
      MediaConfigWithDefaults,
      | 'actualSize'
      | 'fitToDimension'
      | 'mode'
      | 'pixelated'
      | 'gridSettings'
      | 'gallerySettings'
    >;
    mediaType: MediaCardType | null;
    multiRun?: boolean;
    allMediaKeys: string[];
    sizingSettings: SizingSettings;
    historySampleRate?: number;
    updateConfig: (config: Partial<MediaBrowserPanelConfig>) => void;
  }) => {
    const {allMediaKeys, mediaType, updateConfig, sizingSettings} = props;
    const {config} = props;

    // Make filter options
    const axisOptions = (config.mode === 'gallery' ? AxisStrings : AxisStrings)
      .filter(s => {
        // When in not in multi run mode remove the run options
        return !props.multiRun && s === 'run' ? false : true;
      })
      .filter(s => {
        //           Performance tuning required
        //           This requires adding a cache to useLoadFile so it reuses
        //           the same blob between cards and adding something to the
        //           babylon renderer to reuse the same scene object.
        // return mediaType !== 'object3D' && s === 'camera' ? false : true;
        return s !== 'camera';
      })
      .map(s => {
        let text;
        if (s === 'step') {
          text = 'Log Step';
        } else if (s === 'index') {
          text = 'Example Index';
        } else if (s === 'run') {
          text = 'Run';
        } else {
          text = s;
        }

        return {text, key: s, value: s};
      });

    const xAxis = config.gridSettings && config.gridSettings.xAxis;
    const yAxis = config.gridSettings && config.gridSettings.yAxis;
    const galleryAxis = config.gallerySettings && config.gallerySettings.axis;

    const basicTab = (
      <div>
        <Form className="settings__section">
          <div className="panel-setting__item panel-setting__item--stacked">
            <h5 className="input-label">Chart Title</h5>
            <Input
              fluid
              value={config.chartTitle || ''}
              onChange={_.debounce((e, {value}) => {
                updateConfig({
                  chartTitle: value,
                });
              })}
            />
          </div>

          {
            <div className="panel-setting__item panel-setting__item--stacked">
              <h5 className="input-label">Media Key(s)</h5>
              <Form.Field className="panel-setting">
                <ModifiedDropdown
                  style={{width: '100%'}}
                  data-test="media-key-selector"
                  placeholder="Select Media Key"
                  multiple
                  enableReordering
                  search
                  selection
                  options={allMediaKeys.map(k => {
                    return {key: k, value: k, text: k};
                  })}
                  value={config.mediaKeys}
                  onChange={(e, {value}) => {
                    // Convert singletons to array
                    const updateValue = _.isArray(value)
                      ? (value as string[])
                      : [value as string];

                    updateConfig({mediaKeys: updateValue});
                  }}
                />
              </Form.Field>
            </div>
          }

          {sizingSettings.columnCount.max !== sizingSettings.columnCount.min &&
            !config.actualSize &&
            !config.fitToDimension && (
              <>
                <div className="panel-setting__item panel-setting__item--stacked">
                  <h5 className="input-label">Columns</h5>
                  <SliderInput
                    sliderInPopup
                    min={sizingSettings.columnCount.min}
                    max={sizingSettings.columnCount.max}
                    minLabel={sizingSettings.columnCount.min.toString()}
                    maxLabel={sizingSettings.columnCount.max.toString()}
                    step={1}
                    value={sizingSettings.columnCount.current}
                    debounceTime={50}
                    hasInput
                    onChange={configValue =>
                      updateConfig({columnCount: configValue})
                    }
                  />
                </div>
                {config.mode === 'gallery' ? (
                  <div className="panel-setting__item panel-setting__item--stacked">
                    <h5 className="input-label">
                      Max Item Count
                      <HelpPopup helpText="The maximum number of items that will be display in the panel" />
                    </h5>
                    <SliderInput
                      sliderInPopup
                      min={0}
                      max={108}
                      minLabel={'0'}
                      maxLabel={'108'}
                      step={1}
                      value={config.maxGalleryItems}
                      debounceTime={50}
                      hasInput
                      onChange={configValue =>
                        updateConfig({maxGalleryItems: configValue})
                      }
                    />
                  </div>
                ) : (
                  <div className="panel-setting__item panel-setting__item--stacked">
                    <h5 className="input-label">Rows</h5>
                    <SliderInput
                      sliderInPopup
                      min={0}
                      max={50}
                      minLabel={'0'}
                      maxLabel={'50'}
                      step={1}
                      value={config.maxYAxisCount}
                      debounceTime={50}
                      hasInput
                      onChange={configValue =>
                        updateConfig({maxYAxisCount: configValue})
                      }
                    />
                  </div>
                )}
              </>
            )}

          {(mediaType === 'image' || mediaType === 'video') &&
            config.mode !== 'grid' && (
              <>
                <div
                  className="panel-setting__item"
                  onClick={() =>
                    updateConfig({
                      actualSize: !config.actualSize,
                      fitToDimension: false,
                    })
                  }>
                  <Checkbox toggle checked={config.actualSize} />
                  <span className="panel-setting__toggle-label">
                    Original size
                  </span>
                </div>
                {mediaType === 'image' && (
                  <div
                    className="panel-setting__item"
                    onClick={() =>
                      updateConfig({
                        fitToDimension: !config.fitToDimension,
                        actualSize: false,
                      })
                    }>
                    <Checkbox toggle checked={config.fitToDimension} />
                    <span className="panel-setting__toggle-label">
                      Fit to one dimension
                      <HelpPopup helpText="Fit one dimension of your media to the panel and scroll along the other one." />
                    </span>
                  </div>
                )}
              </>
            )}
        </Form>
      </div>
    );

    // Actions
    const swapMode = () => {
      if (config.mode === 'grid') {
        updateConfig({mode: 'gallery'});
      } else {
        updateConfig({mode: 'grid', actualSize: false, fitToDimension: false});
      }
    };

    const advancedTab = (
      <>
        {mediaType === 'image' && (
          <div
            className="panel-setting__item"
            onClick={() => updateConfig({pixelated: !config.pixelated})}>
            <Checkbox toggle checked={!config.pixelated} />
            <span className="panel-setting__toggle-label">Smooth Image</span>
          </div>
        )}

        <div className="panel-setting__item" onClick={swapMode}>
          <Checkbox
            disabled={
              config.actualSize === true || config.fitToDimension === true
            }
            toggle
            checked={config.mode === 'grid'}
          />
          <span className="panel-setting__toggle-label">Grid Mode</span>
        </div>

        {config.mode === 'gallery' && (
          <>
            <h5 className="input-label">Columns</h5>
            <ModifiedDropdown
              data-test="media-key-selector"
              placeholder="Columns"
              options={axisOptions}
              value={galleryAxis}
              selection
              onChange={(e, {value}) => {
                // Convert singletons to array
                const axis = value as Axis;

                updateConfig({
                  gallerySettings: {
                    axis,
                  },
                });
              }}
            />
          </>
        )}

        <div className="panel-setting__item panel-setting__item--stacked">
          <h5 className="input-label">Stride Length</h5>
          <Input
            value={config.stepStrideLength}
            type="number"
            onChange={(e, {value}) => {
              updateConfig({
                stepStrideLength: parseInt(value, 0),
              });
            }}
          />
        </div>

        {config.mode === 'grid' && (
          <>
            <div className="panel-setting__item panel-setting__item--stacked">
              <h5 className="input-label">X-Axis</h5>
              <ModifiedDropdown
                data-test="media-key-selector"
                placeholder="X-Axis"
                // Hide the currently selected option in the counter axis
                options={axisOptions}
                value={xAxis}
                selection
                onChange={(e, {value}) => {
                  // Convert singletons to array
                  const axis = value as Axis;

                  // When the same axis is picked for both cause a swap
                  if (axis === yAxis) {
                    updateConfig({
                      gridSettings: {
                        ...config.gridSettings,
                        yAxis: xAxis,
                        xAxis: axis,
                      },
                    });
                  } else {
                    updateConfig({
                      gridSettings: {
                        ...config.gridSettings,
                        xAxis: axis,
                      },
                    });
                  }
                }}
              />
            </div>
            <div className="panel-setting__item panel-setting__item--stacked">
              <h5 className="input-label">Y-Axis</h5>
              <ModifiedDropdown
                data-test="media-key-selector"
                placeholder="Row"
                options={axisOptions}
                value={yAxis}
                selection
                onChange={(e, {value}) => {
                  const axis = value as Axis;

                  // When the same axis is picked for both cause a swap
                  if (axis === xAxis) {
                    updateConfig({
                      gridSettings: {
                        ...config.gridSettings,
                        yAxis: axis,
                        xAxis: yAxis,
                      },
                    });
                  } else {
                    updateConfig({
                      gridSettings: {
                        ...config.gridSettings,
                        yAxis: axis,
                      },
                    });
                  }
                }}
              />
            </div>
          </>
        )}
        {props.historySampleRate && props.historySampleRate !== 1 && (
          <Message>
            {`Log Steps are sampled at a rate of ~${parseFloat(
              props.historySampleRate.toString()
            ).toFixed(3)} steps per sample`}
          </Message>
        )}
      </>
    );
    const baseTabs = [
      {
        menuItem: 'Basic',
        render: () => (
          <Tab.Pane as="div" className="form-grid">
            {basicTab}
          </Tab.Pane>
        ),
      },
      {
        menuItem: 'Advanced',
        render: () => (
          <Tab.Pane as="div" className="form-grid">
            {advancedTab}
          </Tab.Pane>
        ),
      },
    ];

    const moleculeTab = {
      menuItem: 'Molecular',
      render: () => {
        const mConfig = config.moleculeConfig || {};

        const updateMConfig = (
          newPartialConfig: Partial<MediaBrowserPanelConfig['moleculeConfig']>
        ) =>
          updateConfig({
            moleculeConfig: {
              ...mConfig,
              ...newPartialConfig,
            },
          });

        const {representation} = mConfig;

        return (
          <Tab.Pane as="div" className="form-grid">
            Representation Style
            <Dropdown
              value={representation ?? 'default'}
              options={makeOptions(RepresentationTypeValues)}
              onChange={(e, v) => {
                if (typeof v.value === 'string') {
                  updateMConfig({
                    representation: v.value as RepresentationType,
                  });
                }
              }}
            />
          </Tab.Pane>
        );
      },
    };
    const tabs = baseTabs;

    if (mediaType === 'molecule') {
      tabs.push(moleculeTab);
    }
    return (
      <Tab
        panes={tabs}
        menu={{
          secondary: true,
          pointing: true,
          className: 'chart-settings-tab-menu',
        }}></Tab>
    );
  },
  {id: 'PanelSettings'}
);

const MaskControlComponent = makeComp(
  (p: {
    mediaKey: string;
    maskKey: string;
    classID: string;
    className: string;
    maskConfig: AllMaskControls;
    updateConfig: (v: Partial<MediaBrowserPanelConfig>) => void;
  }) => {
    const {classID, className, mediaKey, maskKey, maskConfig, updateConfig} = p;

    const configPath = ['toggles', mediaKey, maskKey, classID];

    const maskControl: MaskControl =
      _.get(maskConfig, configPath) ??
      (classID === 'all'
        ? DEFAULT_ALL_MASK_CONTROL
        : DEFAULT_CLASS_MASK_CONTROL);

    const update = (newControl: Partial<MaskControl>) => {
      const newConfig = produce(maskConfig, draft => {
        return _.setWith(
          draft,
          configPath,
          {...maskControl, ...newControl},
          Object
        );
      });
      updateConfig({segmentationMaskConfig: newConfig});
    };

    const maskColor =
      classID === 'all' ? [0, 0, 0] : segmentationMaskColor(Number(classID));

    const [color, backgroundColor, borderColor] = maskControl.disabled
      ? ['white', 'grey', 'transparent']
      : classID === 'all'
      ? ['black', 'white', 'black']
      : [
          'black',
          `rgb(${maskColor[0]}, ${maskColor[1]}, ${maskColor[2]})`,
          'transparent',
        ];

    const onClickHandler = () => update({disabled: !maskControl.disabled});

    const button =
      classID === 'all' ? (
        <LegacyWBIcon
          style={{marginLeft: -4, marginBottom: 4, cursor: 'pointer'}}
          size="large"
          onClick={onClickHandler}
          name={maskControl.disabled ? 'hide' : 'show'}
        />
      ) : (
        <div
          key={className}
          onClick={onClickHandler}
          className={classNames(classID !== 'all' && 'mask-control__button')}
          style={{
            color,
            backgroundColor,
            borderColor,
          }}>
          {className}
        </div>
      );

    return (
      <SliderInput
        className={'panel-media__class-slider'}
        key={'slider' + className}
        sliderInPopup
        min={0}
        minLabel={'0'}
        max={1}
        maxLabel={'1'}
        step={0.01}
        value={maskControl.opacity}
        onChange={v => {
          update({opacity: v});
        }}
        debounceTime={10}
        trigger={button}
      />
    );
  },
  {id: 'MaskControlComponent'}
);

const BBoxControlComponent = makeComp(
  (p: {
    mediaKey: string;
    boxKey: string;
    classID: string;
    className: string;
    boxConfig: AllBoundingBoxControls;
    updateConfig: (v: Partial<MediaBrowserPanelConfig>) => void;
  }) => {
    const {classID, className, mediaKey, boxKey, boxConfig, updateConfig} = p;

    const configPath = ['toggles', mediaKey, boxKey, classID];

    const boxControl: BoundingBoxClassControl =
      _.get(boxConfig, configPath) ??
      (classID === 'all'
        ? DEFAULT_ALL_MASK_CONTROL
        : DEFAULT_CLASS_MASK_CONTROL);

    const update = (newControl: Partial<BoundingBoxClassControl>) => {
      const newConfig = produce(boxConfig, draft => {
        return _.setWith(
          draft,
          configPath,
          {...boxControl, ...newControl},
          Object
        );
      });
      updateConfig({boundingBoxConfig: newConfig});
    };

    const classColor =
      classID === 'all' ? [0, 0, 0] : segmentationMaskColor(Number(classID));

    const [color, backgroundColor, borderColor] = boxControl.disabled
      ? ['white', 'grey', 'transparent']
      : classID === 'all'
      ? ['black', 'white', 'black']
      : [
          'black',
          `rgb(${classColor[0]}, ${classColor[1]}, ${classColor[2]})`,
          'transparent',
        ];

    const onClickHandler = () => update({disabled: !boxControl.disabled});

    const button =
      classID === 'all' ? (
        <LegacyWBIcon
          style={{marginLeft: -4, marginBottom: 4, cursor: 'pointer'}}
          size="large"
          onClick={onClickHandler}
          name={boxControl.disabled ? 'hide' : 'show'}
        />
      ) : (
        <div
          key={className}
          onClick={onClickHandler}
          className={classNames(
            classID !== 'all' && 'mask-control__button',
            'panel-media__class-slider'
          )}
          style={{
            color,
            backgroundColor,
            borderColor,
          }}>
          {className}
        </div>
      );

    return button;
  },
  {id: 'BBoxControlComponent'}
);

const styleOptions = [
  {
    key: 'line',
    icon: 'line-solid',
  },
  {
    key: 'dotted',
    icon: 'line-dot',
  },
  {
    key: 'dashed',
    icon: 'line-dash',
  },
];

const STYLE_POPUP_CLASS = 'line-style-buttons';

const BBoxStyleControl = makeComp(
  (p: {
    mediaKey: string;
    boxKey: string;
    boxConfig: AllBoundingBoxControls;
    updateConfig: (v: Partial<MediaBrowserPanelConfig>) => void;
  }) => {
    const {mediaKey, boxKey, boxConfig, updateConfig} = p;

    const style = boxConfig.styles?.[mediaKey]?.[boxKey] ?? defaultBoxStyle;

    const updateStyle = (newStyle: Style) => {
      const newConfig = produce(boxConfig, draft => {
        // We need to set a default layout to perform updates
        _.set(draft, ['styles', mediaKey, boxKey], {...style, ...newStyle});
        return draft;
      });

      updateConfig({boundingBoxConfig: newConfig});
    };

    const activeMarkOption =
      styleOptions.find(o => o.key === style.lineStyle) || styleOptions[0];

    return (
      <Popup
        offset={-12}
        className="line-style-picker-popup"
        on="click"
        trigger={
          <LegacyWBIcon
            style={{marginLeft: 8}}
            name={activeMarkOption.icon}
            className="line-style-picker"
          />
        }>
        <Button.Group className="line-style-buttons">
          {styleOptions.map(markOption => (
            <Button
              size="tiny"
              active={markOption.key === style.lineStyle}
              className="wb-icon-button only-icon"
              key={markOption.key}
              onClick={() => {
                updateStyle({lineStyle: markOption.key as LineStyle});
              }}>
              <LegacyWBIcon name={markOption.icon} />
            </Button>
          ))}
        </Button.Group>
      </Popup>
    );
  },
  {id: 'BBoxStyleControl'}
);

const maskOptionsForLayout = (
  layoutType: LayoutType | null,
  maskKeys: string[]
): MaskOptions[] => {
  if (layoutType === null) {
    layoutType = 'MASKS_NEXT_TO_IMAGE';
  }
  if (layoutType === 'ALL_STACKED') {
    return [{maskKeys, showImage: true}];
  } else if (layoutType === 'ALL_SPLIT') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      ..._.map(maskKeys, k => ({
        maskKeys: [k],
        showImage: false,
      })),
    ];
  } else if (layoutType === 'MASKS_NEXT_TO_IMAGE') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      {
        maskKeys,
        showImage: false,
      },
    ];
  } else {
    // This case should never be hit it is used to hint the type system
    throw new Error('INVALID LAYOUT TYPE');
  }
};

const defaultLayout = (maskKeys: string[]) => {
  return {
    type: DEFAULT_LAYOUT_TYPE as LayoutType,
    maskOptions: maskOptionsForLayout(DEFAULT_LAYOUT_TYPE, maskKeys),
  };
};

const keysToTitle = (keys: string[]) => {
  return keys.length < 2 ? keys[0] : keys.join(', ');
};

const gridRow = (s: any) => <div className="panel-media__grid-row">{s}</div>;

function getTitleFromConfig(config: MediaBrowserPanelConfig): string {
  return (
    config.chartTitle ||
    (config.mediaKeys != null ? keysToTitle(config.mediaKeys) : PANEL_TYPE)
  );
}

export const Spec: Panels.PanelSpec<
  typeof PANEL_TYPE,
  MediaBrowserPanelConfig
> = {
  type: PANEL_TYPE,
  Component: MediaBrowserPanel,
  transformQuery: MediaBrowserPanel.transformQuery,
  getTitleFromConfig,
  configSpec: {
    chartTitle: {editor: 'string', displayName: 'Chart title'},
    columnCount: {
      editor: 'slider',
      displayName: 'Columns',
      min: 1,
      max: 20,
      step: 1,
      default: 4,
    },
    actualSize: {
      editor: 'checkbox',
      displayName: 'Original size',
      default: false,
    },
    fitToDimension: {
      editor: 'checkbox',
      displayName: 'Cover fit',
      default: false,
    },
    pixelated: {editor: 'checkbox', displayName: 'Pixelated', default: true},
    mode: {
      editor: 'dropdown',
      displayName: 'Display as',
      default: 'gallery',
      options: [
        {text: 'Gallery', value: 'gallery'},
        {text: 'Grid', value: 'grid'},
      ],
    },
  },
  icon: 'panel-images',
};

export const ShowMoreContainer = makeComp(
  (props: {iconSize?: IconSizeProp; children: JSX.Element[]}) => {
    const [open, setOpen] = useState<boolean>(false);
    const iconSize = props.iconSize;

    const iconProps = {size: iconSize, onClick: () => setOpen(!open)};

    return (
      <div style={{display: 'flex', width: 600}}>
        <LegacyWBIcon
          {...iconProps}
          name="next"
          style={{
            cursor: 'pointer',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
          className="open show-more-container-toggle"
        />
        <div
          style={{
            maxHeight: open ? undefined : 34,
            overflow: 'hidden',
          }}>
          {props.children}
        </div>
      </div>
    );
  },
  {id: 'ShowMoreContainer'}
);

const calcGroupCount = (config: MediaBrowserPanelConfig): number => {
  return _.sum(
    _.map(config.mediaKeys, m => {
      const tileCount = _.get(
        config.tileLayout,
        [m, 'maskOptions', 'length'],
        1
      );
      return tileCount;
    })
  );
};

const defaultBoxStyle = {lineStyle: 'line'};

function getMaskOptions(
  config: Omit<MediaBrowserPanelConfig, 'chartTitle'>,
  allMasks: {
    [mediaKey: string]: {
      [maskKey: string]: ClassLabelNode;
    };
  },
  t: EmptyTile | Tile
): MaskOptions[] {
  const maskKeys = Object.keys(allMasks[t.mediaKey] ?? {});
  const tileLayout = _.get(
    config,
    ['tileLayout', t.mediaKey],
    defaultLayout(maskKeys)
  ) as {
    type: string;
    maskOptions: MaskOptions[];
  };
  return tileLayout.maskOptions;
}
